"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import type { BestTimeEntry, BestTimeWeek } from "@/lib/conditions/best-time-data";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import { bestTimeSafetyQualification } from "@/lib/conditions/best-time-safety";
import {
  weekConditionsBand,
  weekConditionsCellText,
  weekConditionsTitle,
  busynessBand,
  priceBand,
  formatWeekLabel,
  generateWhyLine,
  overallScoreDisplay,
  countryFlag,
  calcOverallScore,
  getMonthLabel,
  getMonthBoundaries,
} from "./best-time-utils";

type Props = {
  entry: BestTimeEntry;
  rank: number | null;
  bestWeekIdx: number;
  overallScore: number;
  weights: { wC: number; wQ: number; wV: number };
  windowStart?: number;
  windowEnd?: number;
  hasPricing: boolean;
  sport: string;
  safetyState: ConditionSafetyState | null;
};

export function bestTimeResultHref(slug: string, sport: string): string {
  // Canonical spot URL; a named profile lens rides in the fragment.
  return isSupProfileKey(sport)
    ? `/conditions/${slug}#sport=${sport}`
    : `/conditions/${slug}`;
}

function HeatmapStrip({
  weeks,
  bestWeekIdx,
  windowStart,
  windowEnd,
  onExpand,
}: {
  weeks: BestTimeWeek[];
  bestWeekIdx: number;
  windowStart?: number;
  windowEnd?: number;
  onExpand: () => void;
}) {
  const hasWindow = windowStart !== undefined && windowEnd !== undefined;
  const monthBounds = useMemo(() => getMonthBoundaries(weeks), [weeks]);

  return (
    <button
      onClick={onExpand}
      className="w-full mt-3 cursor-pointer group"
      aria-label="Show weekly breakdown"
    >
      <div className="flex">
        {weeks.map((w, i) => {
          const inWindow = !hasWindow || (i >= windowStart && i <= windowEnd);
          const isBest = i === bestWeekIdx;
          const band = weekConditionsBand(w);
          const isMonthBound = monthBounds.has(i);

          return (
            <div
              key={w.weekStart}
              className="flex-1 flex flex-col"
              style={{ marginLeft: isMonthBound ? 3 : i === 0 ? 0 : 1 }}
            >
              <div
                className={`h-4 rounded-[1px] transition-opacity flex items-center justify-center ${isBest ? "ring-2 ring-[#f5c543]" : ""}`}
                style={{
                  backgroundColor: band.colour,
                  opacity: inWindow ? 1 : 0.25,
                }}
                title={weekConditionsTitle(w)}
              >
                <span
                  className={`text-[8px] leading-none font-medium transition-opacity ${isBest ? "opacity-100" : "opacity-50 group-hover:opacity-100"}`}
                  style={{ color: band.fg }}
                >
                  {w.conditionsMissing ? "" : weekConditionsCellText(w)}
                </span>
              </div>
              <div
                className="h-[5px] rounded-[1px] transition-opacity mt-[1px]"
                style={{
                  backgroundColor: busynessBand(w.crowdPressure).colour,
                  opacity: inWindow ? 1 : 0.25,
                }}
              />
            </div>
          );
        })}
      </div>
      {/* Month labels */}
      <div className="flex mt-1">
        {weeks.map((w, i) => {
          const isMonthBound = monthBounds.has(i);
          const showLabel = i === 0 || isMonthBound;
          return (
            <div
              key={w.weekStart}
              className="flex-1"
              style={{ marginLeft: isMonthBound ? 3 : i === 0 ? 0 : 1 }}
            >
              {showLabel && (
                <span className="text-[10px] text-text-muted leading-none">
                  {getMonthLabel(w.weekStart)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-text-muted/60 mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-center">
        Tap for weekly breakdown
      </div>
    </button>
  );
}

function HeatmapGrid({
  weeks,
  weights,
  windowStart,
  windowEnd,
  hasPricing,
  onClose,
}: {
  weeks: BestTimeWeek[];
  weights: { wC: number; wQ: number; wV: number };
  windowStart?: number;
  windowEnd?: number;
  hasPricing: boolean;
  onClose: () => void;
}) {
  const hasWindow = windowStart !== undefined && windowEnd !== undefined;
  const filteredWeeks = hasWindow
    ? weeks.slice(windowStart, windowEnd + 1)
    : weeks;

  const monthBounds = useMemo(() => getMonthBoundaries(filteredWeeks), [filteredWeeks]);

  const priceRange = useMemo(() => {
    if (!hasPricing) return { min: 0, max: 0 };
    let min = Infinity;
    let max = 0;
    for (const w of weeks) {
      if (w.priceEur !== undefined) {
        if (w.priceEur < min) min = w.priceEur;
        if (w.priceEur > max) max = w.priceEur;
      }
    }
    return { min: min === Infinity ? 0 : min, max };
  }, [weeks, hasPricing]);

  const rows: {
    label: string;
    cells: { bg: string; fg: string; text: string }[];
  }[] = [];

  // Conditions row
  rows.push({
    label: "Conditions",
    cells: filteredWeeks.map((w) => {
      const band = weekConditionsBand(w);
      return { bg: band.colour, fg: band.fg, text: weekConditionsCellText(w) };
    }),
  });

  // Crowds row
  rows.push({
    label: "Crowds",
    cells: filteredWeeks.map((w) => {
      const band = busynessBand(w.crowdPressure);
      const shortLabel = w.crowdPressure >= 0.90 ? "V.Busy"
        : w.crowdPressure >= 0.75 ? "Busy"
        : w.crowdPressure >= 0.50 ? "Mod"
        : "Quiet";
      return { bg: band.colour, fg: "#ffffff", text: shortLabel };
    }),
  });

  // Price row (conditional)
  if (hasPricing && filteredWeeks.some((w) => w.priceEur !== undefined)) {
    rows.push({
      label: "Price",
      cells: filteredWeeks.map((w) => {
        if (w.priceEur === undefined) return { bg: "#e5e7eb", fg: "#7A7A7A", text: "n/a" };
        const band = priceBand(w.priceEur, priceRange.min, priceRange.max);
        return { bg: band.colour, fg: band.fg, text: `€${Math.round(w.priceEur)}` };
      }),
    });
  }

  // Overall row
  rows.push({
    label: "Overall",
    cells: filteredWeeks.map((w) => {
      const score = calcOverallScore(w, weights);
      const display = overallScoreDisplay(score);
      return { bg: display.colour, fg: display.textColour, text: String(display.pct) };
    }),
  });

  return (
    <div className="mt-3 border-t border-border-light pt-3">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-semibold text-text-muted uppercase tracking-wide">
          Weekly breakdown
        </span>
        <button
          onClick={onClose}
          className="text-[11px] text-gold hover:text-gold-light font-medium"
        >
          Close
        </button>
      </div>
      <div className="overflow-x-auto -mx-1 px-1">
        {/* Month labels */}
        <div className="flex mb-1" style={{ paddingLeft: 60 }}>
          {filteredWeeks.map((w, i) => {
            const isMonthBound = monthBounds.has(i);
            const showLabel = i === 0 || isMonthBound;
            return (
              <div
                key={w.weekStart}
                className="flex-1 min-w-[28px]"
                style={{ marginLeft: isMonthBound ? 3 : i === 0 ? 0 : 1 }}
              >
                {showLabel && (
                  <span className="text-[10px] text-text-muted leading-none">
                    {getMonthLabel(w.weekStart)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {/* Data rows */}
        {rows.map((row) => (
          <div key={row.label} className="flex items-center mb-[2px]">
            <span className="w-[60px] shrink-0 text-[11px] font-semibold text-text-muted uppercase tracking-wide">
              {row.label}
            </span>
            <div className="flex flex-1">
              {row.cells.map((cell, i) => {
                const isMonthBound = monthBounds.has(i);
                return (
                  <div
                    key={i}
                    className="flex-1 min-w-[28px] h-8 rounded-[2px] flex items-center justify-center"
                    style={{
                      backgroundColor: cell.bg,
                      marginLeft: isMonthBound ? 3 : i === 0 ? 0 : 1,
                    }}
                  >
                    <span
                      className="text-[9px] md:text-[10px] font-medium leading-none"
                      style={{ color: cell.fg }}
                    >
                      {cell.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ResultCard({
  entry,
  rank,
  bestWeekIdx,
  overallScore,
  weights,
  windowStart,
  windowEnd,
  hasPricing,
  sport,
  safetyState,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  const bestWeek = entry.weeks[bestWeekIdx];
  const weekLabel = formatWeekLabel(bestWeek.weekStart);
  const scoreDisplay = overallScoreDisplay(overallScore);
  const whyLine = safetyState === "flagged-red"
    ? "Historical conditions shown for reference. This destination is not included in the numbered recommendations while a red safety flag is present."
    : generateWhyLine(bestWeek, weekLabel, bestWeek.conditions);
  const safetyQualification = bestTimeSafetyQualification(safetyState);

  const avgPrice =
    entry.pricingAvailable && entry.weeks.some((w) => w.priceEur !== undefined)
      ? Math.round(
          entry.weeks.reduce((sum, w) => sum + (w.priceEur ?? 0), 0) /
            entry.weeks.filter((w) => w.priceEur !== undefined).length
        )
      : undefined;

  return (
    <div className="bg-white border border-border-light rounded-[14px] p-4 md:p-5 hover:border-gold/30 hover:shadow-[0_8px_24px_rgba(0,0,0,0.05)] transition-all">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link
            href={bestTimeResultHref(entry.slug, sport)}
            prefetch={false}
            className="flex items-center gap-2 no-underline group"
          >
            <span className="text-[13px] font-bold text-text-muted/50 w-6 shrink-0">
              {rank ?? "–"}
            </span>
            <span className="text-[15px] shrink-0">
              {countryFlag(entry.countryCode)}
            </span>
            <span className="font-semibold text-navy text-[15px] group-hover:text-gold transition-colors truncate">
              {entry.name}
            </span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              className="opacity-30 shrink-0"
            >
              <path
                d="M5 3l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>

          <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-8">
            {safetyQualification && (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  safetyState === "flagged-red"
                    ? "bg-red-50 text-red-700"
                    : safetyState === "flagged-amber"
                      ? "bg-amber-50 text-amber-800"
                      : "bg-slate-100 text-slate-600"
                }`}
              >
                {safetyQualification}
              </span>
            )}
            {entry.tags.map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-warm-bg text-text-muted text-[10px] font-medium rounded-full"
              >
                {tag}
              </span>
            ))}
            {entry.pisteKm && (
              <span className="text-[11px] text-text-muted">
                {entry.pisteKm} km
              </span>
            )}
            {avgPrice !== undefined && (
              <span className="text-[11px] text-text-muted">
                ~€{avgPrice}/wk
              </span>
            )}
          </div>

          <p className="text-[12px] italic text-text-muted/80 mt-2 ml-8 leading-[1.5]">
            {whyLine}
          </p>
        </div>

        <div className="flex flex-col items-center shrink-0">
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-[15px]${scoreDisplay.pct >= 75 ? " shadow-[0_0_12px_rgba(245,197,67,0.4)]" : ""}`}
            style={{ backgroundColor: scoreDisplay.colour, color: scoreDisplay.textColour }}
          >
            {scoreDisplay.pct}
          </div>
          <span className="text-[8px] font-bold uppercase tracking-wide text-text-muted/60 mt-1">
            Overall
          </span>
          <span className="text-[10px] text-text-muted mt-0.5 text-center">
            {weekLabel}
          </span>
        </div>
      </div>

      {!expanded && (
        <HeatmapStrip
          weeks={entry.weeks}
          bestWeekIdx={bestWeekIdx}
          windowStart={windowStart}
          windowEnd={windowEnd}
          onExpand={() => setExpanded(true)}
        />
      )}

      {expanded && (
        <HeatmapGrid
          weeks={entry.weeks}
          weights={weights}
          windowStart={windowStart}
          windowEnd={windowEnd}
          hasPricing={hasPricing}
          onClose={() => setExpanded(false)}
        />
      )}
    </div>
  );
}
